import React, { useMemo } from 'react';

import { NotesPanel } from '@/components/NotesPanel';
import type { KnipperCaseState } from '@/constants/caseStates';
import { useNotes } from '@/hooks/useNotes';
import { useUsersByIds } from '@/hooks/useUsers';
import { useStore } from '@/store/store';
import { formatDate } from '@/utils/date';

interface CaseStateReasonSectionProps {
  caseId: string;
  caseState: KnipperCaseState;
  /** User ID who performed the state change (cancelled_by or updated_by) */
  actionByUserId?: string;
  /** Timestamp when the state was changed */
  actionAt?: string;
}

/**
 * Displays reason/notes for terminal case states (duplicate, cancelled, non_reportable).
 * Shows NotesPanel if notes exist, otherwise shows a compact single-line message.
 */
export function CaseStateReasonSection({
  caseId,
  caseState,
  actionByUserId,
  actionAt,
}: CaseStateReasonSectionProps) {
  const { timeZone } = useStore();

  // Fetch notes for this case
  const { data: notesData, isLoading: isLoadingNotes } = useNotes(caseId);
  const notes = useMemo(() => notesData?.notes || [], [notesData?.notes]);
  const hasNotes = notes.length > 0;

  // Fetch user info for the action performer
  const userIds = useMemo(() => (actionByUserId ? [actionByUserId] : []), [actionByUserId]);
  const { usersMap, isLoading: isLoadingUser } = useUsersByIds(userIds, !!actionByUserId);

  const actionByUserName = useMemo(() => {
    if (!actionByUserId) return null;
    const user = usersMap.get(actionByUserId);
    return user?.name || user?.email || null;
  }, [actionByUserId, usersMap]);

  // Determine title based on state (duplicate vs cancelled/non_reportable)
  const notesTitle = caseState === 'duplicate' ? 'Duplicate Reason' : 'Cancellation Reason';

  // Determine action verb for the compact display
  const actionVerb = caseState === 'duplicate' ? 'Marked as Duplicate' : 'Cancelled';

  // Show loading state while checking for notes
  if (isLoadingNotes) {
    return (
      <div className="bg-card border rounded-lg mb-4 px-4 py-3">
        <div className="animate-pulse h-4 bg-muted rounded w-48" />
      </div>
    );
  }

  // If there are notes, show the full NotesPanel
  if (hasNotes) {
    return (
      <div className="bg-card border rounded-lg mb-4">
        <NotesPanel
          caseRecordId={caseId}
          canAddNote={false}
          showFilters={false}
          title={notesTitle}
        />
      </div>
    );
  }

  // No notes - show compact single-line info
  return (
    <div className="bg-card border rounded-lg mb-4 px-4 py-3">
      <p className="text-sm font-medium">
        {isLoadingUser ? (
          <span className="animate-pulse text-muted-foreground">Loading...</span>
        ) : actionByUserName && actionAt ? (
          <>
            {actionVerb} by {actionByUserName} on {formatDate(actionAt, timeZone)}
          </>
        ) : actionAt ? (
          <>
            {actionVerb} on {formatDate(actionAt, timeZone)}
          </>
        ) : (
          actionVerb
        )}
      </p>
    </div>
  );
}
